import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, AlertCircle, FileText, Bold, Italic, Underline, List, ListOrdered, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminAddBlog() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    date: new Date().toISOString().split("T")[0],
    author: "Ashish Bhave",
    linkedin_url: "https://www.linkedin.com/in/ashish-bhave",
    image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&w=800&q=80",
    extract: "",
    content: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [slugModifiedManually, setSlugModifiedManually] = useState(false);
  const editorRef = useRef(null);

  const execCommand = (command, value = "") => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      handleEditorChange();
    }
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setFormData((prev) => ({
        ...prev,
        content: html,
      }));
    }
  };

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w\-]+/g, "") // Remove all non-word chars
      .replace(/\-\-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start
      .replace(/-+$/, ""); // Trim - from end
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "title" && !slugModifiedManually) {
      setFormData((prev) => ({
        ...prev,
        title: value,
        slug: slugify(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const formatImageUrl = (url) => {
    if (!url) return "";
    let cleanUrl = url.trim();
    
    // Convert Unsplash browser page URLs to optimized CDN URLs
    if (cleanUrl.includes("unsplash.com/photos/")) {
      try {
        const parts = cleanUrl.split("?")[0].split("/");
        const photoId = parts[parts.length - 1];
        if (photoId) {
          return `https://images.unsplash.com/photo-${photoId}?auto=format&fit=crop&w=800&q=80`;
        }
      } catch (e) {
        // fallback
      }
    }
    
    // Standardize direct Unsplash CDN URLs and append optimal params
    if (cleanUrl.includes("images.unsplash.com")) {
      try {
        const baseUrl = cleanUrl.split("?")[0];
        return `${baseUrl}?auto=format&fit=crop&w=800&q=80`;
      } catch (e) {
        return cleanUrl;
      }
    }
    return cleanUrl;
  };

  const handleImageBlur = (e) => {
    const formatted = formatImageUrl(e.target.value);
    setFormData((prev) => ({
      ...prev,
      image: formatted,
    }));
  };

  const handleSlugChange = (e) => {
    setSlugModifiedManually(true);
    setFormData((prev) => ({
      ...prev,
      slug: slugify(e.target.value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Simple validation
    if (!formData.title.trim()) {
      setError("Blog Title is required.");
      setLoading(false);
      return;
    }
    if (!formData.slug.trim()) {
      setError("Slug is required.");
      setLoading(false);
      return;
    }
    if (!formData.author.trim()) {
      setError("Author Name is required.");
      setLoading(false);
      return;
    }
    if (!formData.extract.trim()) {
      setError("Short extract is required.");
      setLoading(false);
      return;
    }
    if (!formData.content.trim()) {
      setError("Blog body content is required.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/blogs", formData);
      // On success, redirect to the newly created blog's detail page
      navigate(`/blogs/${response.data.slug}`);
    } catch (err) {
      console.error("Error creating blog:", err);
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to create blog. Please check your backend connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header Breadcrumb */}
        <div className="flex items-center justify-between">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-800 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Insights</span>
          </Link>
          <div className="text-xs text-sif-muted font-bold uppercase tracking-wider bg-slate-100 border border-sif px-3 py-1 rounded-full">
            Admin console
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-2xl border border-sif card-shadow overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-6 border-b border-indigo-900">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-indigo-400" />
              <span>Create New SIF Insight Blog</span>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">
              Add news, market analysis, regulatory updates, or informative articles to the platform.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
            
            {/* Error Banner */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start gap-3 shadow-xs">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-red-800 text-sm">Submission Error</h4>
                  <p className="text-xs sm:text-sm text-red-700 mt-0.5">{error}</p>
                </div>
              </div>
            )}

            {/* Grid fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Title */}
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Blog Title</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Navigating SEBI's Latest Direct Plan Circulars"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                  required
                />
              </div>

              {/* Slug */}
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>URL Slug</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  placeholder="slugified-url-path"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm font-mono text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                  required
                />
                <p className="text-[10px] text-sif-muted">
                  The URL path for this blog will be: <code className="text-indigo-500 font-semibold font-mono">/blogs/{formData.slug || "your-slug"}</code>
                </p>
              </div>

              {/* Date */}
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Publication Date</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                  required
                />
              </div>

              {/* Author */}
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Author Name</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="e.g. Ashish Bhave"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                  required
                />
              </div>

              {/* LinkedIn URL */}
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Author LinkedIn Profile URL</span>
                </label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder="e.g. https://www.linkedin.com/in/username"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                />
              </div>

              {/* Cover Image URL */}
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Cover Image URL</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  onBlur={handleImageBlur}
                  placeholder="e.g. https://images.unsplash.com/... or https://unsplash.com/photos/..."
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition"
                  required
                />
              </div>

              {/* Extract / Short Description */}
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <span>Short Extract / Card Snippet</span>
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="extract"
                  rows={3}
                  value={formData.extract}
                  onChange={handleInputChange}
                  placeholder="Write a brief 2-3 sentence teaser to display on the blog listing card..."
                  className="w-full px-3.5 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition resize-y"
                  required
                />
              </div>

              {/* Full Content */}
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-extrabold uppercase tracking-wider text-slate-700 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <span>Full Blog Body Content (Rich Text Editor)</span>
                    <span className="text-red-500">*</span>
                  </span>
                </label>
                
                <style dangerouslySetInnerHTML={{__html: `
                  .prose-editor[contenteditable]:empty:before {
                    content: attr(placeholder);
                    color: #94a3b8;
                    cursor: text;
                  }
                  .prose-editor {
                    font-weight: 400 !important;
                  }
                  .prose-editor strong, .prose-editor b, .prose-editor [style*="font-weight: bold"] {
                    font-weight: 800 !important;
                  }
                  .prose-editor h2 {
                    font-size: 1.6rem !important;
                    font-weight: 800 !important;
                    color: #0f172a !important;
                    margin-top: 1.5rem !important;
                    margin-bottom: 0.75rem !important;
                    display: block !important;
                  }
                  .prose-editor h3 {
                    font-size: 1.25rem !important;
                    font-weight: 700 !important;
                    color: #1e293b !important;
                    margin-top: 1.25rem !important;
                    margin-bottom: 0.5rem !important;
                    display: block !important;
                  }
                  .prose-editor ul {
                    list-style-type: disc !important;
                    padding-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                  }
                  .prose-editor ol {
                    list-style-type: decimal !important;
                    padding-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                  }
                  .prose-editor li {
                    display: list-item !important;
                  }
                `}} />

                <div className="border border-sif rounded-lg overflow-hidden bg-slate-50 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white transition flex flex-col shadow-xs">
                  {/* Toolbar */}
                  <div className="bg-slate-100 border-b border-sif p-2 flex flex-wrap gap-1.5 items-center select-none">
                    <button
                      type="button"
                      onClick={() => execCommand("bold")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 transition"
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("italic")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 transition"
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("underline")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 transition"
                      title="Underline"
                    >
                      <Underline className="w-4 h-4" />
                    </button>
                    <div className="w-px h-5 bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand("formatBlock", "<h2>")}
                      className="px-2.5 py-1 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 font-extrabold text-xs transition"
                      title="Heading 2"
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("formatBlock", "<h3>")}
                      className="px-2.5 py-1 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 font-extrabold text-xs transition"
                      title="Heading 3"
                    >
                      H3
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("formatBlock", "<p>")}
                      className="px-2.5 py-1 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 text-xs transition font-bold"
                      title="Normal Text"
                    >
                      Text
                    </button>
                    <div className="w-px h-5 bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand("insertUnorderedList")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 transition"
                      title="Bullet List"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("insertOrderedList")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-indigo-600 transition"
                      title="Numbered List"
                    >
                      <ListOrdered className="w-4 h-4" />
                    </button>
                    <div className="w-px h-5 bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand("removeFormat")}
                      className="p-1.5 rounded hover:bg-slate-200 text-slate-700 hover:text-red-600 transition"
                      title="Clear Formatting"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Content editable canvas */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleEditorChange}
                    onBlur={handleEditorChange}
                    className="p-4 min-h-[300px] max-h-[500px] overflow-y-auto bg-white text-slate-800 text-base focus:outline-none prose max-w-none prose-editor font-normal"
                    placeholder="Write the full content of the blog post. Formats and lists apply dynamically..."
                    style={{ outline: "none" }}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <Link
                to="/blogs"
                className="px-5 py-2.5 border border-sif text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-semibold transition"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition duration-200"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Publish Blog Post</span>
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
